/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { CreditCard, Zap, CircleAlert, CircleCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { simulateStripe } from '@/lib/devTools';

const OUTCOMES = [
  ['success', 'success ✓'], ['card_declined', 'card_declined ✗'], ['insufficient_funds', 'insufficient_funds ✗'],
  ['3ds_required', '3ds_required ⚡'], ['rate_limit', 'rate_limit ⏱'],
];

const NumberField = ({ label, value, onChange, step, testId }) => (
  <div>
    <Label className="text-[10px]">{label}</Label>
    <Input type="number" step={step} className="h-7 text-xs" value={value} onChange={onChange} data-testid={testId} />
  </div>
);

const StripeResult = ({ r }) => (
  <div className={`rounded-md p-2 text-[10px] font-mono ${r.status === 'succeeded' ? 'bg-green-500/15 text-green-800 dark:text-green-300' : 'bg-red-500/15 text-red-800 dark:text-red-300'}`} data-testid="stripe-result">
    {r.status === 'succeeded' ? <CircleCheck className="inline h-3 w-3 mr-1" /> : <CircleAlert className="inline h-3 w-3 mr-1" />}
    <span className="font-bold uppercase">{r.outcome}</span>
    {r.status === 'succeeded' && (
      <>
        <div>base: € {r.computation.base_eur.toFixed(2)}</div>
        <div>final: € {r.computation.final_eur.toFixed(2)} (-{r.computation.applied_discount_pct}%)</div>
        <div>fee: € {r.computation.platform_fee_eur.toFixed(2)} · medico: € {r.computation.doctor_amount_eur.toFixed(2)}</div>
        <div>savings: € {r.computation.savings_eur.toFixed(2)}</div>
      </>
    )}
    {r.status === 'failed' && <div>{r.error?.message}</div>}
  </div>
);

/** Tab "Gateway": simulatore Stripe (family + premium + fee). */
export const GatewaysTab = ({ busy, withBusy }) => {
  const [form, setForm] = useState({ base_price_eur: 30, family_discount_pct: 15, premium_discount_pct: 0, platform_fee_pct: 15, outcome: 'success' });
  const [result, setResult] = useState(null);
  const num = (k, parse) => (e) => setForm((f) => ({ ...f, [k]: parse(e.target.value || 0) }));
  const run = () => withBusy(async () => setResult(await simulateStripe(form)), 'Simulazione Stripe fallita');

  return (
    <section className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-center gap-2"><CreditCard className="h-3.5 w-3.5" /><div className="text-xs font-bold">Stripe Simulator</div></div>
      <div className="text-[10px] text-muted-foreground">Riproduce il calcolo family + premium usato in <code>create_appointment</code>.</div>
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="Prezzo base €" step="0.01" value={form.base_price_eur} onChange={num('base_price_eur', parseFloat)} testId="stripe-base-price" />
        <NumberField label="Family %" value={form.family_discount_pct} onChange={num('family_discount_pct', (v) => parseInt(v, 10))} testId="stripe-family-pct" />
        <NumberField label="Premium %" value={form.premium_discount_pct} onChange={num('premium_discount_pct', (v) => parseInt(v, 10))} />
        <NumberField label="Fee platform %" value={form.platform_fee_pct} onChange={num('platform_fee_pct', (v) => parseInt(v, 10))} />
      </div>
      <div>
        <Label className="text-[10px]">Esito</Label>
        <select className="flex h-7 w-full items-center rounded-md border border-input bg-background px-2 text-xs" value={form.outcome} onChange={(e) => setForm((f) => ({ ...f, outcome: e.target.value }))} data-testid="stripe-outcome">
          {OUTCOMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <Button size="sm" onClick={run} disabled={busy} className="w-full" data-testid="stripe-run-btn"><Zap className="h-3 w-3 mr-1" /> Esegui simulazione</Button>
      {result && <StripeResult r={result} />}
    </section>
  );
};
